import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "./api/api";

function DataTable({ title, rows, customStatusLogic, invDate, setInvDate, fetchData, onStatusClick }) {
  const headerControls = (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-3 gap-3 md:gap-0 w-full">
      <div className="flex items-center gap-2">
        <label htmlFor="invoice-date-dt" className="text-xs font-medium text-gray-700">Invoice Date:</label>
        <input
          id="invoice-date-dt"
          type="date"
          value={invDate}
          onChange={(e) => setInvDate(e.target.value)}
          className="text-xs border border-gray-300 px-2 py-1 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 w-32"
        />
        <button
          onClick={fetchData}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
        >
          Refresh
        </button>
      </div>
      <h2 className="text-sm font-bold">{title}</h2>
    </div>
  );

  if (!rows || rows.length === 0) {
    return (
      <section className="w-full flex-1 flex flex-col items-center justify-start bg-white p-4 rounded-lg shadow-md border border-gray-200 mt-4">
        {headerControls}
        <p className="text-gray-500 text-xs italic mt-4">No data found.</p>
      </section>
    );
  }

  const columnsToHide = ['CPId', 'Itemid'];
  const allHeaders = Object.keys(rows[0]);
  const visibleDataHeaders = allHeaders.filter(header => !columnsToHide.includes(header));
  let headersToDisplay = [...visibleDataHeaders];
  if (customStatusLogic && !headersToDisplay.includes("Status")) {
    headersToDisplay.push("Status");
  }

  // 🔹 Sort rows by status priority
  const statusOrder = {
    "Waiting for Mapping": 1,
    "Pending Mapping": 2,
    "Mapping Completed": 3,
    "Unknown": 4,
  };

  const sortedRows = [...rows].sort((a, b) => {
    const statusA = customStatusLogic(a).text;
    const statusB = customStatusLogic(b).text;
    return (statusOrder[statusA] || 99) - (statusOrder[statusB] || 99);
  });

  return (
    <section className="w-full flex-1 flex flex-col bg-white p-3 rounded-lg shadow-md border border-gray-200 mt-4">
      {headerControls}
      <div className="flex-1 overflow-auto border border-gray-300 rounded-lg shadow-inner max-h-[calc(100vh-160px)]">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-200 text-gray-700 sticky top-0 z-10">
              {headersToDisplay.map((h) => (
                <th
                  key={h}
                  className="px-3 py-1 text-left font-semibold border-b border-gray-300 whitespace-nowrap text-xs"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => {
              const statusInfo = customStatusLogic(row);
              const isClickable =
                statusInfo.text === "Waiting for Mapping" || statusInfo.text === "Pending Mapping";
              return (
                <tr key={i} className="hover:bg-gray-50">
                  {visibleDataHeaders.map((h) => (
                    <td key={h} className="px-3 py-1 border-b border-gray-200 whitespace-nowrap text-xs">
                      {row[h] ?? "-"}
                    </td>
                  ))}
                  {customStatusLogic && (
                    <td className="px-3 py-1 border-b border-gray-200 whitespace-nowrap text-xs">
                      <button
                        onClick={isClickable ? () => onStatusClick(row, statusInfo.text) : undefined}
                        className={`${statusInfo.className} ${isClickable ? "cursor-pointer hover:shadow-sm" : "cursor-not-allowed opacity-70"
                          } text-xs`}
                        disabled={!isClickable}
                      >
                        {statusInfo.text}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InvoiceMapping() {
  //const user = JSON.parse(localStorage.getItem("user") || "{}");
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  
  const username = user.UsrName || "";
  const navigate = useNavigate();
  const location = useLocation();

  const [invDate, setInvDate] = useState(new Date().toISOString().slice(0, 10));
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const getStatusText = (row) => {
    if (row.InvQty === row.BalQty) {
      return {
        text: "Waiting for Mapping",
        className: "bg-gray-500 text-white px-2 py-0.5 rounded text-xs w-32 text-center",
      };
    } else if (row.AllotedQty > 0 && row.BalQty > 0) {
      return {
        text: "Pending Mapping",
        className: "bg-yellow-500 text-black px-2 py-0.5 rounded text-xs w-32 text-center",
      };
    } else if (row.BalQty === 0) {
      return {
        text: "Mapping Completed",
        className: "bg-green-500 text-white px-2 py-0.5 rounded text-xs w-32 text-center",
      };
    }
    return {
      text: "Unknown",
      className: "bg-gray-400 text-black px-2 py-0.5 rounded text-xs w-32 text-center",
    };
  };

  const fetchData = async (dateToFetch) => {
    if (!username || !dateToFetch) {
      console.warn("Username or date is missing, cannot fetch data.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.JCInvoiceMappingList(username, dateToFetch);
      setTableData(res.Table || []);
    } catch (error) {
      console.error("Error fetching invoice mapping:", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const navState = location.state;
    let dateToUse = invDate;

    if (navState?.refresh && navState?.selectedDate) {
      dateToUse = navState.selectedDate;
      setInvDate(navState.selectedDate);
      navigate('.', { state: {}, replace: true });
    }

    fetchData(dateToUse);
  }, []);

  const handleStatusClick = (row) => {
    if (!row.Itemid) return;
    navigate("/InvoiceMappingAlloted", {
      state: {
        FGitemId: row.Itemid,
        ReqMapQty: row.BalQty,
        CPId: row.CPId,
        returnDate: invDate,
      },
    });
  };

  const handleBackToMenu = () => {
    navigate("/Menu");
  };

  return (
    <div className="w-full h-screen p-4 bg-gray-50 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-semibold text-gray-800">Invoice Mapping</h1>
        <button
          onClick={handleBackToMenu}
          className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md shadow-sm"
        >
          Back to Menu
        </button>
      </div>
      <DataTable
        title="Invoice Mapping Details"
        rows={tableData}
        customStatusLogic={getStatusText}
        invDate={invDate}
        setInvDate={setInvDate}
        fetchData={() => fetchData(invDate)}
        onStatusClick={handleStatusClick}
      />
      {loading && (
        <p className="text-xs text-center text-gray-700 font-medium flex-1 flex items-center justify-center mt-2">
          Loading data...
        </p>
      )}
    </div>
  );
}

export default InvoiceMapping;
